import React, { useRef, useCallback, useState, useEffect } from 'react';
import { faFileWord, faFileExcel, faFilePowerpoint, faFileArchive, faFileCode, faFilePdf, faFile, faFileAudio, faFileVideo, faFileImage, faFileAlt } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BigFileIcon, { defaultStyles } from 'react-file-icon';
import Dropzone from 'react-dropzone';
import loadImage from 'blueimp-load-image';

/**
 * An icon of a paperclip, which is used as the default icon for FileUploadButton
 */
var AttachmentIcon = function () { return (React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "24", height: "20", viewBox: "0 0 24 24" },
    React.createElement("path", { d: "M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5a2.5 2.5 0 0 1 5 0v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5a2.5 2.5 0 0 0 5 0V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z" }),
    React.createElement("path", { d: "M0 0h24v24H0z", fill: "none" }))); };

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

// Partially based on:
// https://stackoverflow.com/a/4212908/2570866
var wordMimeTypes = [
    // Microsoft Word
    // .doc .dot
    'application/msword',
    // .doc .dot
    'application/msword-template',
    // .docx
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    // .dotx (no test)
    'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
    // .docm
    'application/vnd.ms-word.document.macroEnabled.12',
    // .dotm (no test)
    'application/vnd.ms-word.template.macroEnabled.12',
    // LibreOffice/OpenOffice Writer
    // .odt
    'application/vnd.oasis.opendocument.text',
    // .ott
    'application/vnd.oasis.opendocument.text-template',
    // .fodt
    'application/vnd.oasis.opendocument.text-flat-xml',
    // .uot
    // NOTE: firefox doesn't know mimetype so maybe ignore
];
var excelMimeTypes = [
    // .csv
    'text/csv',
    // TODO: maybe more data files
    // Microsoft Excel
    // .xls .xlt .xla (no test for .xla)
    'application/vnd.ms-excel',
    // .xlsx
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    // .xltx (no test)
    'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
    // .xlsm
    'application/vnd.ms-excel.sheet.macroEnabled.12',
    // .xltm (no test)
    'application/vnd.ms-excel.template.macroEnabled.12',
    // .xlam (no test)
    'application/vnd.ms-excel.addin.macroEnabled.12',
    // .xlsb (no test)
    'application/vnd.ms-excel.addin.macroEnabled.12',
    // LibreOffice/OpenOffice Calc
    // .ods
    'application/vnd.oasis.opendocument.spreadsheet',
    // .ots
    'application/vnd.oasis.opendocument.spreadsheet-template',
    // .fods
    'application/vnd.oasis.opendocument.spreadsheet-flat-xml',
    // .uos
    // NOTE: firefox doesn't know mimetype so maybe ignore
];
var powerpointMimeTypes = [
    // Microsoft Word
    // .ppt .pot .pps .ppa (no test for .ppa)
    'application/vnd.ms-powerpoint',
    // .pptx
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    // .potx (no test)
    'application/vnd.openxmlformats-officedocument.presentationml.template',
    // .ppsx
    'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
    // .ppam
    'application/vnd.ms-powerpoint.addin.macroEnabled.12',
    // .pptm
    'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
    // .potm
    'application/vnd.ms-powerpoint.template.macroEnabled.12',
    // .ppsm
    'application/vnd.ms-powerpoint.slideshow.macroEnabled.12',
    // LibreOffice/OpenOffice Writer
    // .odp
    'application/vnd.oasis.opendocument.presentation',
    // .otp
    'application/vnd.oasis.opendocument.presentation-template',
    // .fodp
    'application/vnd.oasis.opendocument.presentation-flat-xml',
    // .uop
    // NOTE: firefox doesn't know mimetype so maybe ignore
];
var archiveFileTypes = [
    // .zip
    'application/zip',
    // .z7
    'application/x-7z-compressed',
    // .ar
    'application/x-archive',
    // .tar
    'application/x-tar',
    // .tar.gz
    'application/gzip',
    // .tar.Z
    'application/x-compress',
    // .tar.bz2
    'application/x-bzip',
    // .tar.lz
    'application/x-lzip',
    // .tar.lz4
    'application/x-lz4',
    // .tar.lzma
    'application/x-lzma',
    // .tar.lzo (no test)
    'application/x-lzop',
    // .tar.xz
    'application/x-xz',
    // .war
    'application/x-webarchive',
    // .rar
    'application/vnd.rar',
];
var codeFileTypes = [
    // .html .htm
    'text/html',
    // .css
    'text/css',
    // .js
    'application/x-javascript',
    // .json
    'application/json',
    // .py
    'text/x-python',
    // .go
    'text/x-go',
    // .c
    'text/x-csrc',
    // .cpp
    'text/x-c++src',
    // .rb
    'application/x-ruby',
    // .rust
    'text/rust',
    // .java
    'text/x-java',
    // .php
    'application/x-php',
    // .cs
    'text/x-csharp',
    // .scala
    'text/x-scala',
    // .erl
    'text/x-erlang',
    // .sh
    'application/x-shellscript',
];
var mimeTypeToIconMap = {
    'application/pdf': faFilePdf,
};
var mimeTypeToFallBackStyle = {
    'application/pdf': defaultStyles.pdf,
};
for (var _i = 0, wordMimeTypes_1 = wordMimeTypes; _i < wordMimeTypes_1.length; _i++) {
    var type = wordMimeTypes_1[_i];
    mimeTypeToIconMap[type] = faFileWord;
    mimeTypeToFallBackStyle[type] = { type: 'document' };
}
for (var _a = 0, excelMimeTypes_1 = excelMimeTypes; _a < excelMimeTypes_1.length; _a++) {
    var type = excelMimeTypes_1[_a];
    mimeTypeToIconMap[type] = faFileExcel;
    mimeTypeToFallBackStyle[type] = { type: 'spreadsheet' };
}
for (var _b = 0, powerpointMimeTypes_1 = powerpointMimeTypes; _b < powerpointMimeTypes_1.length; _b++) {
    var type = powerpointMimeTypes_1[_b];
    mimeTypeToIconMap[type] = faFilePowerpoint;
    mimeTypeToFallBackStyle[type] = { type: 'presentation' };
}
for (var _c = 0, archiveFileTypes_1 = archiveFileTypes; _c < archiveFileTypes_1.length; _c++) {
    var type = archiveFileTypes_1[_c];
    mimeTypeToIconMap[type] = faFileArchive;
    mimeTypeToFallBackStyle[type] = { type: 'compressed' };
}
for (var _d = 0, codeFileTypes_1 = codeFileTypes; _d < codeFileTypes_1.length; _d++) {
    var type = codeFileTypes_1[_d];
    mimeTypeToIconMap[type] = faFileCode;
    mimeTypeToFallBackStyle[type] = { type: 'code' };
}
function mimeTypeToIcon(mimeType) {
    if (mimeType == null) {
        return faFile;
    }
    var icon = mimeTypeToIconMap[mimeType];
    if (icon) {
        return icon;
    }
    if (mimeType.startsWith('audio/')) {
        return faFileAudio;
    }
    if (mimeType.startsWith('video/')) {
        return faFileVideo;
    }
    if (mimeType.startsWith('image/')) {
        return faFileImage;
    }
    if (mimeType.startsWith('text/')) {
        return faFileAlt;
    }
    return faFile;
}
function mimeTypeToStyle(mimeType) {
    if (mimeType == null) {
        return {};
    }
    var style = mimeTypeToFallBackStyle[mimeType];
    if (style) {
        return style;
    }
    if (mimeType.startsWith('audio/')) {
        return { type: 'audio' };
    }
    if (mimeType.startsWith('video/')) {
        return { type: 'video' };
    }
    if (mimeType.startsWith('image/')) {
        return { type: 'image' };
    }
    if (mimeType.startsWith('text/')) {
        return { type: 'document' };
    }
    return {};
}
function fileExtension(filename) {
    var _a, _b;
    var defaultReturn = {
        full: '',
        end: '',
    };
    if (filename == null) {
        return defaultReturn;
    }
    // source: https://stackoverflow.com/a/1203361/2570866
    var a = filename.split('.');
    if (a.length === 1 || (a[0] === '' && a.length === 2)) {
        return defaultReturn;
    }
    var lastExtension = (_a = a.pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    if (a.length === 1 || (a[0] === '' && a.length === 2)) {
        return { end: lastExtension, full: lastExtension };
    }
    var secondToLastExtension = (_b = a.pop()) === null || _b === void 0 ? void 0 : _b.toLowerCase();
    if (secondToLastExtension !== 'tar') {
        return { end: lastExtension, full: lastExtension };
    }
    return {
        full: secondToLastExtension + '.' + lastExtension,
        end: lastExtension,
    };
}
var FileIcon = function (_a) {
    var _b = _a.big, big = _b === void 0 ? false : _b, _c = _a.size, size = _c === void 0 ? 50 : _c, filename = _a.filename, mimeType = _a.mimeType;
    if (big) {
        var extension = fileExtension(filename);
        var style = extension.end && defaultStyles[extension.end];
        if (!style) {
            style = mimeTypeToStyle(style);
        }
        // @ts-expect-error types for file-icon are for newer version, which doesn't have a default export
        return React.createElement(BigFileIcon, __assign({ extension: extension.full }, style, { size: size }));
    }
    return (React.createElement(FontAwesomeIcon, { className: "rfu-file-icon--small", icon: mimeTypeToIcon(mimeType) }));
};

var LoadingIndicator = function (props) {
    var _a = props.size, size = _a === void 0 ? 20 : _a, _b = props.width, width = _b === void 0 ? 2 : _b, backgroundColor = props.backgroundColor, color = props.color;
    return (React.createElement("div", { className: "rfu-loading-indicator__spinner", style: {
            margin: '0 auto',
            borderColor: backgroundColor ? backgroundColor : '',
            borderTopColor: color ? color : '',
            width: size ? size : '',
            height: size ? size : '',
            borderWidth: width ? width : '',
        } }));
};

/**
 * Component that displays files which are being uploaded
 */
var FilePreviewer = function (_a) {
    var uploads = _a.uploads, handleRemove = _a.handleRemove, handleRetry = _a.handleRetry;
    return (React.createElement("div", { className: "rfu-file-previewer" },
        React.createElement("ol", null, uploads &&
            uploads.map(function (upload) { return (React.createElement("li", { key: upload.id, className: "rfu-file-previewer__file " + (upload.state === 'uploading'
                    ? 'rfu-file-previewer__file--uploading'
                    : '') + " " + (upload.state === 'failed'
                    ? 'rfu-file-previewer__file--failed'
                    : '') },
                React.createElement(FileIcon, { mimeType: upload.file.type, filename: upload.file.name }),
                React.createElement("a", { href: upload.url, download: true },
                    upload.file.name,
                    upload.state === 'failed' && (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: "rfu-file-previewer__failed", onClick: handleRetry && (function () { return handleRetry(upload.id); }) }, "failed"),
                        React.createElement("div", { className: "rfu-file-previewer__retry", onClick: handleRetry && (function () { return handleRetry(upload.id); }) }, "retry")))),
                React.createElement("span", { className: "rfu-file-previewer__close-button", onClick: handleRemove && (function () { return handleRemove(upload.id); }) }, "\u2718"),
                upload.state === 'uploading' && (React.createElement("div", { className: "rfu-file-previewer__loading-indicator" },
                    React.createElement(LoadingIndicator, null))))); }))));
};

var FileUploadButton = function (_a) {
    var _b = _a.disabled, disabled = _b === void 0 ? false : _b, _c = _a.multiple, multiple = _c === void 0 ? false : _c, _d = _a.children, children = _d === void 0 ? React.createElement(AttachmentIcon, null) : _d, handleFiles = _a.handleFiles, accepts = _a.accepts;
    var inputRef = useRef(null);
    var className = 'rfu-file-upload-button';
    if (disabled) {
        className = className + " rfu-file-upload-button--disabled";
    }
    return (React.createElement("div", { className: className },
        React.createElement("label", null,
            React.createElement("input", { type: "file", ref: inputRef, className: "rfu-file-input", onChange: function (event) {
                    var files = event.currentTarget.files;
                    if (files) {
                        handleFiles(files);
                    }
                    if (inputRef.current !== null) {
                        inputRef.current.value = '';
                        inputRef.current.blur();
                    }
                }, multiple: multiple, disabled: disabled, accept: Array.isArray(accepts) ? accepts.join(',') : accepts }),
            children)));
};

/**
 * This is simply a button wrapper, add's a div with `role="button"` and a onClick
 */
var IconButton = function (_a) {
    var onClick = _a.onClick, children = _a.children;
    return (React.createElement("div", { className: "rfu-icon-button", role: "button", onClick: onClick }, children));
};

var ImageDropzone = function (_a) {
    var handleFiles = _a.handleFiles, maxNumberOfFiles = _a.maxNumberOfFiles, children = _a.children, accept = _a.accept, multiple = _a.multiple, disabled = _a.disabled;
    var handleDrop = useCallback(function (accepted) {
        if (!handleFiles) {
            return;
        }
        if (accepted && accepted.length) {
            handleFiles(accepted);
        }
    }, [handleFiles]);
    return (React.createElement(Dropzone, { onDrop: handleDrop, maxFiles: maxNumberOfFiles, accept: accept, multiple: multiple, disabled: disabled }, function (_a) {
        var getRootProps = _a.getRootProps, isDragAccept = _a.isDragAccept, isDragReject = _a.isDragReject;
        return (React.createElement("div", __assign({}, getRootProps({
            style: { position: 'relative' },
            className: "\n          rfu-dropzone\n          " + (isDragAccept ? 'rfu-dropzone--accept' : '') + "\n          " + (isDragReject ? 'rfu-dropzone--reject' : '') + "\n        ",
        })),
            React.createElement("div", { className: "rfu-dropzone__notifier" },
                React.createElement("div", { className: "rfu-dropzone__inner" },
                    React.createElement("svg", { width: "41", height: "41", viewBox: "0 0 41 41", xmlns: "http://www.w3.org/2000/svg" },
                        React.createElement("path", { d: "M40.517 28.002V3.997c0-2.197-1.808-3.992-4.005-3.992H12.507a4.004 4.004 0 0 0-3.992 3.993v24.004a4.004 4.004 0 0 0 3.992 3.993h24.005c2.197 0 4.005-1.795 4.005-3.993zm-22.002-7.997l4.062 5.42 5.937-7.423 7.998 10H12.507l6.008-7.997zM.517 8.003V36c0 2.198 1.795 4.005 3.993 4.005h27.997V36H4.51V8.002H.517z", fill: "#000", fillRule: "nonzero" })),
                    React.createElement("p", null, "Drag your files here to add to your post"))),
            children));
    }));
};

var placeholder = "data:image/png;base64,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";

var svg = '<svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><defs><path d="M465 5c5.53 0 10 4.47 10 10s-4.47 10-10 10-10-4.47-10-10 4.47-10 10-10zm3.59 5L465 13.59 461.41 10 460 11.41l3.59 3.59-3.59 3.59 1.41 1.41 3.59-3.59 3.59 3.59 1.41-1.41-3.59-3.59 3.59-3.59-1.41-1.41z" id="b"/><filter x="-30%" y="-30%" width="160%" height="160%" filterUnits="objectBoundingBox" id="a"><feOffset in="SourceAlpha" result="shadowOffsetOuter1"/><feGaussianBlur stdDeviation="2" in="shadowOffsetOuter1" result="shadowBlurOuter1"/><feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.5 0" in="shadowBlurOuter1"/></filter></defs><g transform="translate(-451 -1)" fill-rule="nonzero" fill="none"><use fill="#000" filter="url(#a)" xlink:href="#b"/><use fill="#FFF" fill-rule="evenodd" xlink:href="#b"/></g></svg>';
var Thumbnail = function (props) {
    var id = props.id, image = props.image, _a = props.size, size = _a === void 0 ? 100 : _a, handleClose = props.handleClose;
    var _b = useState(null), imgSrc = _b[0], setImgSrc = _b[1];
    useEffect(function () {
        if (!imgSrc && image) {
            /**
            if the file is already uploaded we don't need to process the image
            we check to see if it's base64 string or a url
            */
            if (!image.indexOf('data:')) {
                return setImgSrc(image);
            }
            loadImage(image, function (img) {
                if (!('toDataURL' in img))
                    return;
                var base64data = img.toDataURL('image/jpeg');
                setImgSrc(base64data);
            }, { orientation: true, crossOrigin: 'anonymous', meta: true });
        }
    }, [imgSrc, image]);
    var onClose = useCallback(function () {
        if (handleClose) {
            handleClose(id);
        }
    }, [id, handleClose]);
    return (React.createElement("div", { className: "rfu-thumbnail__wrapper", style: { width: size, height: size } },
        React.createElement("div", { className: "rfu-thumbnail__overlay" }, handleClose ? (React.createElement(IconButton, { onClick: onClose },
            React.createElement("div", { dangerouslySetInnerHTML: { __html: svg } }))) : null),
        React.createElement("img", { src: imgSrc || image || placeholder, className: "rfu-thumbnail__image", alt: "" })));
};

/**
 * An icon of a picture, which is used as the default icon for ImageUploadButton
 */
var PictureIcon = function () { return (React.createElement("svg", { width: "20", height: "20", viewBox: "0 0 20 20", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("path", { d: "M2 0h16a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2zm3 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM2 16v2h16v-6l-3-3-6 6-3-3-4 4z", fillRule: "nonzero" }))); };

var ImageUploadButton = function (props) {
    var _a = props.multiple, multiple = _a === void 0 ? false : _a, _b = props.disabled, disabled = _b === void 0 ? false : _b, handleFiles = props.handleFiles, _c = props.children, children = _c === void 0 ? React.createElement(PictureIcon, null) : _c;
    return (React.createElement("div", { className: "rfu-image-upload-button" },
        React.createElement("label", null,
            React.createElement("input", { type: "file", className: "rfu-image-input", onChange: function (event) {
                    var files = event.currentTarget.files;
                    if (files) {
                        handleFiles(Array.from(files));
                    }
                }, accept: "image/*", multiple: multiple, disabled: disabled }),
            children)));
};

var ThumbnailPlaceholder = function (props) {
    var _a = props.multiple, multiple = _a === void 0 ? false : _a, handleFiles = props.handleFiles;
    return (React.createElement(ImageUploadButton, { handleFiles: handleFiles, multiple: multiple },
        React.createElement("div", { role: "button", className: "rfu-thumbnail-placeholder" },
            React.createElement("svg", { width: "14", height: "15", viewBox: "0 0 14 15", xmlns: "http://www.w3.org/2000/svg" },
                React.createElement("path", { d: "M14 8.998H8v6H6v-6H0v-2h6v-6h2v6h6z", fill: "#A0B2B8", fillRule: "nonzero" })))));
};

var ImagePreviewer = function (props) {
    var _a = props.multiple, multiple = _a === void 0 ? true : _a, _b = props.disabled, disabled = _b === void 0 ? false : _b, imageUploads = props.imageUploads, handleRemove = props.handleRemove, handleRetry = props.handleRetry, handleFiles = props.handleFiles;
    var onClose = useCallback(function (id) {
        if (handleRemove) {
            if (id == null) {
                console.warn("id of closed image was undefined, this shouldn't happen");
                return;
            }
            handleRemove(id);
        }
    }, [handleRemove]);
    return (React.createElement("div", { className: "rfu-image-previewer" }, imageUploads === null || imageUploads === void 0 ? void 0 :
        imageUploads.map(function (image) {
            var url = image.url || image.previewUri;
            return (React.createElement("div", { key: image.id, className: "rfu-image-previewer__image" + (image.state === 'finished'
                    ? ' rfu-image-previewer__image--loaded'
                    : '') },
                image.state === 'failed' && (React.createElement("div", { className: "rfu-image-previewer__retry", dangerouslySetInnerHTML: {
                        __html: '<svg width="22" height="20" viewBox="0 0 22 20" xmlns="http://www.w3.org/2000/svg"><path d="M20 5.535V2a1 1 0 0 1 2 0v6a1 1 0 0 1-1 1h-6a1 1 0 0 1 0-2h3.638l-2.975-2.653a8 8 0 1 0 1.884 8.32 1 1 0 1 1 1.886.666A10 10 0 1 1 5.175 1.245c3.901-2.15 8.754-1.462 11.88 1.667L20 5.535z" fill="#FFF" fill-rule="nonzero"/></svg>',
                    }, onClick: handleRetry && (function () { return handleRetry(image.id); }) })),
                url !== undefined && (React.createElement(Thumbnail, { handleClose: onClose, image: url, id: image.id })),
                image.state === 'uploading' && (React.createElement(LoadingIndicator, { backgroundColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.7)" }))));
        }),
        handleFiles && !disabled && (React.createElement(ThumbnailPlaceholder, { handleFiles: handleFiles, multiple: multiple }))));
};

function dataTransferItemsHaveFiles(items) {
    if (!items || !items.length) {
        return false;
    }
    for (var _i = 0, items_1 = items; _i < items_1.length; _i++) {
        var item = items_1[_i];
        if (item.kind === 'file' || item.type === 'text/html') {
            return true;
        }
    }
    return false;
}
function getFileLikes(items) {
    var fileLikes = [];
    for (var _i = 0, items_2 = items; _i < items_2.length; _i++) {
        var item = items_2[_i];
        if (item.kind === 'file') {
            var file = item.getAsFile();
            if (file) {
                fileLikes.push(file);
            }
        }
    }
    return fileLikes;
}
function getImageSource(fileLikes, src) {
    return __awaiter(this, void 0, void 0, function () {
        var res, e_1, contentType, buf, blob;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, fetch(src)];
                case 1:
                    res = _a.sent();
                    return [3 /*break*/, 3];
                case 2:
                    e_1 = _a.sent();
                    return [2 /*return*/];
                case 3:
                    contentType = res.headers.get('Content-type') || 'application/octet-stream';
                    return [4 /*yield*/, res.arrayBuffer()];
                case 4:
                    buf = _a.sent();
                    blob = new Blob([buf], { type: contentType });
                    fileLikes.push(blob);
                    return [2 /*return*/];
            }
        });
    });
}
var extractImageSources = function (s) {
    var imageTags = new DOMParser()
        .parseFromString(s, 'text/html')
        .getElementsByTagName('img');
    return Array.from(imageTags, function (tag) { return tag.src; }).filter(function (tag) { return tag; });
};
function dataTransferItemsToFiles(items) {
    return __awaiter(this, void 0, void 0, function () {
        var fileLikes, blobPromises, _loop_1, _i, items_3, item;
        var _this = this;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!items || !items.length) {
                        return [2 /*return*/, []];
                    }
                    fileLikes = getFileLikes(items);
                    if (fileLikes.length) {
                        return [2 /*return*/, fileLikes];
                    }
                    blobPromises = [];
                    _loop_1 = function (item) {
                        if (item.type === 'text/html') {
                            blobPromises.push(new Promise(function (accept) {
                                item.getAsString(function (s) { return __awaiter(_this, void 0, void 0, function () {
                                    var imagePromises;
                                    return __generator(this, function (_a) {
                                        switch (_a.label) {
                                            case 0:
                                                imagePromises = extractImageSources(s).map(function (src) {
                                                    return getImageSource(fileLikes, src);
                                                });
                                                return [4 /*yield*/, Promise.all(imagePromises)];
                                            case 1:
                                                _a.sent();
                                                accept();
                                                return [2 /*return*/];
                                        }
                                    });
                                }); });
                            }));
                        }
                    };
                    for (_i = 0, items_3 = items; _i < items_3.length; _i++) {
                        item = items_3[_i];
                        _loop_1(item);
                    }
                    return [4 /*yield*/, Promise.all(blobPromises)];
                case 1:
                    _a.sent();
                    return [2 /*return*/, fileLikes];
            }
        });
    });
}

export { AttachmentIcon, FileIcon, FilePreviewer, FileUploadButton, IconButton, ImageDropzone, ImagePreviewer, ImageUploadButton, LoadingIndicator, PictureIcon, Thumbnail, ThumbnailPlaceholder, dataTransferItemsHaveFiles, dataTransferItemsToFiles };
//# sourceMappingURL=index.es.js.map
